import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, CreditCard, FileText, QrCode, Download, 
  Shield, Clock, Smartphone, Heart, AlertTriangle,
  Phone, Pill, Hospital, CheckCircle
} from 'lucide-react';

const HowItWorksPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-600 to-red-700">
      {/* Header */}
      <div className="bg-red-700 text-white p-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1">
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-xl font-bold">How It Works</h1>
          <p className="text-red-200 text-sm">Simple 5-step guide</p>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Intro */}
        <Card className="bg-white/95">
          <CardContent className="p-4 text-center">
            <Clock className="h-10 w-10 text-red-600 mx-auto mb-2" />
            <h2 className="text-lg font-bold text-slate-800">Takes only 3 minutes!</h2>
            <p className="text-slate-600 text-sm">Create your emergency medical card in 5 easy steps</p>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="bg-white/95 border-l-4 border-red-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                1
              </div>
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-red-600" />
                  Open the App
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Tap <strong>"Create Your Card - $1.00"</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="bg-white/95 border-l-4 border-green-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                2
              </div>
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  Pay $1 (One-Time)
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Use Card, Apple Pay, or Google Pay<br/>
                  <span className="text-green-600 font-medium">No subscription - pay once, use forever!</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="bg-white/95 border-l-4 border-blue-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                3
              </div>
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Fill Your Info
                </h3>
                <div className="text-slate-600 text-sm mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-red-500" />
                    <span><strong>Required:</strong> Name, Blood Group</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span>Allergies (with severity)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-pink-500" />
                    <span>Medical Conditions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4 text-blue-500" />
                    <span>Current Medications</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Hospital className="h-4 w-4 text-purple-500" />
                    <span>Previous Surgeries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-500" />
                    <span>Emergency Contacts (up to 3)</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 4 */}
        <Card className="bg-white/95 border-l-4 border-purple-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                4
              </div>
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <QrCode className="h-4 w-4 text-purple-600" />
                  Create Your Card
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Tap <strong>"Create Emergency Card"</strong> button<br/>
                  Your QR code is generated instantly!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 5 */}
        <Card className="bg-white/95 border-l-4 border-amber-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0">
                5
              </div>
              <div>
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Download className="h-4 w-4 text-amber-600" />
                  Save Your QR Code!
                </h3>
                <p className="text-slate-600 text-sm mt-1 mb-2">
                  <span className="text-red-600 font-medium">Important: We don't store your data!</span>
                </p>
                <div className="bg-amber-50 p-2 rounded text-sm space-y-1">
                  <p>✓ Download to your phone</p>
                  <p>✓ Set as lock screen wallpaper</p>
                  <p>✓ Print for your wallet</p>
                  <p>✓ Share with family</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What happens when scanned */}
        <Card className="bg-green-50 border border-green-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-green-800 flex items-center gap-2 mb-2">
              <QrCode className="h-5 w-5" />
              When Someone Scans Your QR
            </h3>
            <p className="text-green-700 text-sm">
              Anyone can scan with their phone camera - <strong>no app needed!</strong>
            </p>
            <p className="text-green-700 text-sm mt-2">
              They'll see your name, age, blood type, allergies, medications, 
              and a <strong>CALL button</strong> for your emergency contacts.
            </p>
          </CardContent>
        </Card>

        {/* Privacy */}
        <Card className="bg-slate-100 border border-slate-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-slate-600" />
              Your Privacy is Protected
            </h3>
            <p className="text-slate-600 text-sm">
              Your medical data is stored <strong>inside the QR code itself</strong> - 
              not on our servers. We can't see or access your information.
            </p>
            <p className="text-slate-700 text-sm mt-2 font-medium">
              You own your data. Always.
            </p>
          </CardContent>
        </Card>

        {/* CTA */}
        <Button 
          onClick={() => navigate('/create')}
          className="w-full h-14 text-lg bg-white text-red-600 hover:bg-red-50 font-bold"
        >
          Create Your Card Now - $1.00
        </Button>
      </div>
    </div>
  );
};

export default HowItWorksPage;
